import React from 'react'
import { Text, ScrollView, View, useWindowDimensions, TouchableOpacity } from 'react-native'
import { ACSharedStyles } from '../../admin-config-common'
import { useTranslation } from 'react-i18next'
import {
  AppContext,
  HightLightTable,
  HightLightTableColum,
  Permission,
  PulseOpsFormat,
  sharedStyle
} from '@pulseops/common'
import { makeStyles } from '@material-ui/core'
import { OPLSharedStyles } from '@pulseops/submission'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/lib/function'
import { ACApprovalWorkflowService, ACMetricHistoryDetailService } from '../../admin-config-service'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { useLoading } from '@mxt/zio-react'
import { ACApprovalWorkflowForm } from './ACApprovalWorkflowForm'
import { useFieldArray, useForm } from 'react-hook-form'
import { ACApprovalWorkflowConst } from './ACApprovalWorkflowConst'
import { AdministrationConfigContext } from '../../AdministrationConfigContext'

export const ACApprovalWorkflow = () => {
  const { t } = useTranslation()
  const { height, width } = useWindowDimensions()
  const isFocused = useIsFocused()
  const [isLoading, bindLoader] = useLoading(false)
  const rootNavigation = useNavigation()
  const [searchedApprovalList, setSearchedApprovalList] = React.useState<
    ACMetricHistoryDetailService.MetricHistoryDetailInfo[]
  >([])
  const { changeBreadcrumb, showToast, showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const { userPermissions } = React.useContext(AdministrationConfigContext)
  const [pageNum, setPageNum] = React.useState<number>(0)
  const [pageSize, setPageSize] = React.useState<number>(10)
  const [total, setTotal] = React.useState<number>(0)
  const { control, getValues, setValue } = useForm<ACApprovalWorkflowForm.ACApprovalWorkflowData>({
    defaultValues: {
      approvalWorkflowList: []
    }
  })
  const approvalFieldArray = useFieldArray<ACApprovalWorkflowForm.ACApprovalWorkflowData>({
    control: control,
    name: 'approvalWorkflowList'
  })
  const makeTableStyles = makeStyles({
    container: {
      border: `1px solid #D3DCE6`,
      borderWidth: 1,
      borderRadius: 8,
      maxHeight: height - 343,
      maxWidth: width - 40
    }
  })

  const displayedColumns: HightLightTableColum[] = [
    {
      title: t('AdministratorConfig:RequestID'),
      fieldName: 'requestID'
    },
    {
      title: t('AdministratorConfig:Type'),
      fieldName: 'type'
    },
    {
      title: t('AdministratorConfig:Creator'),
      fieldName: 'creator'
    },
    {
      title: t('AdministratorConfig:GroupMetrics'),
      fieldName: 'groupMetrics'
    },
    {
      title: t('AdministratorConfig:MetricName'),
      fieldName: 'metricName'
    },
    {
      title: t('AdministratorConfig:Value'),
      fieldName: 'value'
    },
    {
      title: t('AdministratorConfig:Description'),
      fieldName: 'description'
    },
    {
      title: t('AdministratorConfig:RequestedDate'),
      fieldName: 'requestedDate'
    },
    {
      title: t('AdministratorConfig:OldOperator'),
      fieldName: 'oldOperator',
      disabled: true,
      isHightLightCol: (selectedRow) => {
        const dataItem = selectedRow.selectItem
        const oldOperator = dataItem['oldOperator'].toString()
        const newOperator = dataItem['newOperator'].toString()
        return oldOperator !== newOperator
      }
    },
    {
      title: t('AdministratorConfig:NewOperator'),
      fieldName: 'newOperator',
      disabled: true,
      isHightLightCol: (selectedRow) => {
        const dataItem = selectedRow.selectItem
        const oldOperator = dataItem['oldOperator'].toString()
        const newOperator = dataItem['newOperator'].toString()
        return oldOperator !== newOperator
      }
    },
    {
      title: t('AdministratorConfig:OldOperatorValue'),
      fieldName: 'oldOperatorValue',
      disabled: true,
      isHightLightCol: (selectedRow) => {
        const dataItem = selectedRow.selectItem
        const oldOperatorValue = dataItem['oldOperatorValue'].toString()
        const newOperatorValue = dataItem['newOperatorValue'].toString()
        return oldOperatorValue !== newOperatorValue
      }
    },
    {
      title: t('AdministratorConfig:NewOperatorValue'),
      fieldName: 'newOperatorValue',
      disabled: true,
      isHightLightCol: (selectedRow) => {
        const dataItem = selectedRow.selectItem
        const oldOperatorValue = dataItem['oldOperatorValue'].toString()
        const newOperatorValue = dataItem['newOperatorValue'].toString()
        return oldOperatorValue !== newOperatorValue
      }
    },
    {
      title: t('AdministratorConfig:OldRAGScore'),
      fieldName: 'oldRAGScore',
      disabled: true,
      isHightLightCol: (selectedRow) => {
        const dataItem = selectedRow.selectItem
        const oldRAGScore = dataItem['oldRAGScore'].toString()
        const newRAGScore = dataItem['newRAGScore'].toString()
        return oldRAGScore !== newRAGScore
      }
    },
    {
      title: t('AdministratorConfig:NewRAGScore'),
      fieldName: 'newRAGScore',
      disabled: true,
      isHightLightCol: (selectedRow) => {
        const dataItem = selectedRow.selectItem
        const oldRAGScore = dataItem['oldRAGScore'].toString()
        const newRAGScore = dataItem['newRAGScore'].toString()
        return oldRAGScore !== newRAGScore
      }
    },
    {
      title: t('AdministratorConfig:OldMultiplier'),
      fieldName: 'oldMultiplier',
      disabled: true,
      isHightLightCol: (selectedRow) => {
        const dataItem = selectedRow.selectItem
        const oldMultiplier = dataItem['oldMultiplier'].toString()
        const newMultiplier = dataItem['newMultiplier'].toString()
        return oldMultiplier !== newMultiplier
      }
    },
    {
      title: t('AdministratorConfig:NewMultiplier'),
      fieldName: 'newMultiplier',
      disabled: true,
      isHightLightCol: (selectedRow) => {
        const dataItem = selectedRow.selectItem
        const oldMultiplier = dataItem['oldMultiplier'].toString()
        const newMultiplier = dataItem['newMultiplier'].toString()
        return oldMultiplier !== newMultiplier
      }
    },
    {
      title: t('AdministratorConfig:Action'),
      fieldName: '',
      disabled: true,
      rowRender: (selectedProp) => {
        return (
          <View style={ACSharedStyles.buttonContainer}>
            {userPermissions.includes(Permission.ApproveApprovalWorkflowBSCAdminConfig) && (
              <TouchableOpacity
                style={[sharedStyle.button, sharedStyle.btnRed, OPLSharedStyles.buttonContent]}
                onPress={() => onApproveEvent(selectedProp.index, ACApprovalWorkflowConst.ApprovalStatus.Approved)}
              >
                <Text style={sharedStyle.textButtonRed}>{t('AdministratorConfig:Approve')}</Text>
              </TouchableOpacity>
            )}
            {userPermissions.includes(Permission.RejectApprovalWorkflowBSCAdminConfig) && (
              <TouchableOpacity
                style={[sharedStyle.button, OPLSharedStyles.buttonContent]}
                onPress={() => onRejectEvent(selectedProp.index, ACApprovalWorkflowConst.ApprovalStatus.Rejected)}
              >
                <Text style={sharedStyle.textButton}>{t('AdministratorConfig:Reject')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )
      }
    }
  ]

  React.useEffect(() => {
    if (isFocused) {
      changeBreadcrumb([
        {
          title: '',
          navigate: () => {
            rootNavigation.navigate('HomeScreen')
          }
        },
        {
          title: t('AdministratorConfig:ApprovalWorkflow'),
          navigate: null
        }
      ])
      if (userPermissions && userPermissions.length > 0) {
        if (!userPermissions.includes(Permission.LinkApprovalWorkflowBSCAdminConfig)) {
          rootNavigation.navigate('HomeScreen')
        } else {
          setPageNum(0)
          setPageSize(10)
          getSearchedList(0, 10)
        }
      }
    }
    return () => {
      setSearchedApprovalList([])
    }
  }, [isFocused, userPermissions])

  React.useEffect(() => {
    showGlobalLoading(isLoading)
  }, [isLoading])

  const getSearchedList = (pageNo: number, pageSi: number) => {
    const bodyData: ACMetricHistoryDetailService.ACMetricHistoryDetailRequest = {
      start: pageNo,
      size: pageSi,
      orders: [],
      sort: null,
      status: 'New request',
      createdBy: '',
      groupId: '',
      fromDate: '',
      toDate: '',
      isPaginate: true
    }
    pipe(
      ACMetricHistoryDetailService.getACMetricHistoryDetailList(bodyData),
      ZIO.map((metricResponse) => {
        if (metricResponse.data && metricResponse.data.length > 0) {
          const searchedArr: ACMetricHistoryDetailService.MetricHistoryDetailInfo[] = metricResponse.data.map(
            (historyItem) => {
              const requestedDate = historyItem.createdAt
                ? PulseOpsFormat.dateStringtoFormat(historyItem.createdAt, 'DD/MM/YYYY - HH:mm:ss')
                : ''
              return {
                requestID: historyItem.requestId.toString(),
                status: historyItem.status,
                type: historyItem.type || '',
                creator: historyItem.createdBy,
                requestedDate: requestedDate,
                approver: historyItem.approver || '',
                approvedDate: historyItem.approvedDate || '',
                groupMetrics: historyItem.oldMetric.groupMetrics,
                metricName: historyItem.oldMetric.metricName,
                value: historyItem.oldMetric.value,
                description: historyItem.oldMetric.description,
                oldOperator: historyItem.oldMetric.operator,
                newOperator: historyItem.newOperator,
                oldOperatorValue: historyItem.oldMetric.operatorValue,
                newOperatorValue: historyItem.newOperatorValue,
                oldRAGScore: historyItem.oldMetric.ragThreshold,
                newRAGScore: historyItem.newRagThreshold,
                oldMultiplier: historyItem.oldMetric.multiplier,
                newMultiplier: historyItem.newMultiplier
              }
            }
          )
          setSearchedApprovalList(searchedArr)
          initialApprovalWorkflowForm(searchedArr)
          setTotal(metricResponse.total)
        } else {
          setSearchedApprovalList([])
          setTotal(0)
        }
        return metricResponse
      }),
      ZIO.mapError((error) => {
        setSearchedApprovalList([])
        setTotal(0)
        return error
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  }

  const initialApprovalWorkflowForm = (searchedList: ACMetricHistoryDetailService.MetricHistoryDetailInfo[]) => {
    let i = 0
    approvalFieldArray.remove()
    setValue(`approvalWorkflowList`, [])
    while (i < searchedList.length) {
      approvalFieldArray.append({
        requestId: searchedList[i].requestID,
        status: ''
      })
      setValue(`approvalWorkflowList.${i}.requestId`, searchedList[i].requestID)
      setValue(`approvalWorkflowList.${i}.status`, '')
      i++
    }
  }

  const onApproveEvent = (index: number, status: string) => {
    const requestId = getValues(`approvalWorkflowList.${index}.requestId`)
    const submitData: ACApprovalWorkflowService.ApprovalInfoRequest = {
      requestId: Number(requestId),
      status: status
    }
    pipe(
      ACApprovalWorkflowService.submitApproval(submitData),
      ZIO.map((responseData) => {
        if (responseData.data && responseData.data.length > 0) {
          setPageNum(0)
          setPageSize(10)
          getSearchedList(0, 10)
          showToast(t('message:MS020098'), 'success')
        } else {
          showToast(t('AdministratorConfig:MS030197'), 'error')
        }
        return responseData
      }),
      ZIO.mapError((err) => {
        showToast(t('AdministratorConfig:MS030197'), 'error')
        return err
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  }

  const onRejectEvent = (index: number, status: string) => {
    const requestId = getValues(`approvalWorkflowList.${index}.requestId`)
    const submitData: ACApprovalWorkflowService.ApprovalInfoRequest = {
      requestId: Number(requestId),
      status: status
    }
    pipe(
      ACApprovalWorkflowService.submitReject(submitData),
      ZIO.map((responseData) => {
        if (responseData.data && responseData.data.length > 0) {
          setPageNum(0)
          setPageSize(10)
          getSearchedList(0, 10)
          showToast(t('message:MS020099'), 'success')
        } else {
          showToast(t('AdministratorConfig:MS030197'), 'error')
        }
        return responseData
      }),
      ZIO.mapError((err) => {
        showToast(t('AdministratorConfig:MS030197'), 'error')
        return err
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  }

  const onPageChange = (pageNo: number, pageSi: number) => {
    setPageNum(pageNo)
    getSearchedList(pageNo, pageSi)
  }

  const onRowsPerPageChange = (pageNo: number, pageSi: number) => {
    setPageSize(pageSi)
    getSearchedList(pageNo, pageSi)
  }

  return (
    <View style={ACSharedStyles.wrapper}>
      <ScrollView style={ACSharedStyles.container} horizontal={false}>
        <View style={ACSharedStyles.headingContainer}>
          <Text style={ACSharedStyles.headingText}>{t('AdministratorConfig:ApprovalWorkflow')}</Text>
        </View>
        <HightLightTable
          displayedColumns={displayedColumns}
          dataSource={searchedApprovalList}
          tableContainerClassName={makeTableStyles().container}
          stickyHeader
          isPagination={true}
          totalRecord={total}
          pageSize={pageSize}
          pageNum={pageNum}
          setPageNum={setPageNum}
          setPageSize={setPageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          showPaginationEnTranslation={true}
        ></HightLightTable>
      </ScrollView>
    </View>
  )
}
